import { useState } from "react";
import { Button, Datagrid, DateField, FunctionField, ListContextProvider, TextField, useGetList } from "react-admin";
import HistoryIcon from "@mui/icons-material/History";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import dataProvider from "server/dataProvider";
import { administrationRequests } from "api/administrationApi";
import { settings } from "app";
import { useNotifyError } from "utils/useNotifyError";
import { euroFormat } from "./utils";

// "full tax (3), no tax (1)" - how many paid education rows of the plan carry each tax type.
const formatTaxTypes = (paidEducationFees, taxTypes) => {
  if (!paidEducationFees?.length) return "-";

  const counts = paidEducationFees.reduce((acc, fee) => {
    acc[fee.taxType] = (acc[fee.taxType] ?? 0) + 1;
    return acc;
  }, {});

  return Object.entries(counts)
    .map(([taxType, count]) => {
      const name = taxTypes?.find(t => t.id === Number(taxType))?.nameBg ?? taxType;
      return `${name} (${count})`;
    })
    .join(", ");
};

/**
 * Lists every annual fee plan ever recorded for the speciality, newest cohort first, so an admin can see which
 * admission years are already covered before enrolling a transfer student into an older cohort.
 */
const AnnualFeePlansHistoryDialog = ({ specialityId }) => {
  const notifyError = useNotifyError();
  const [plans, setPlans] = useState([]);
  const { data: taxTypes } = useGetList(administrationRequests.getPaidEducationAnnualTaxTypesSelect());

  const fetchPlans = async () => {
    try {
      const { data } = await dataProvider.customRequest(administrationRequests.getAnnualFeePlansHistory(specialityId));
      setPlans((data?.plans ?? []).map(plan => ({ ...plan, id: plan.planId })));
    } catch (err) {
      notifyError(err);
      return false;
    }
  };

  return (
    <ConfirmModal
      dialogSize="md"
      onOpen={fetchPlans}
      titleContent={"История на годишните такси"}
      isConfirmButton={false}
      triggerButton={
        <Button
          label="История на плановете"
          variant="contained"
          size="medium"
          startIcon={<HistoryIcon />}
        />
      }
    >
      <ListContextProvider value={{ data: plans }}>
        <Datagrid
          rowClick={false}
          bulkActionButtons={false}
          empty={<p style={{ margin: "1rem" }}>Няма въведени годишни такси за тази специалност.</p>}
          sx={{ margin: 2, ...settings.style.secondaryDatagrid }}
        >
          <TextField
            source="admissionYear"
            label="Година на прием"
          />
          <FunctionField
            label="Статус"
            render={plan => (plan.isCurrent ? "Текущ план" : "История")}
          />
          <DateField
            source="createdOn"
            label="Създаден на"
            locales="bg-BG"
          />
          <FunctionField
            label="Държавна поръчка (евро)"
            render={plan =>
              plan.stateEducationFees?.length
                ? plan.stateEducationFees.map(fee => euroFormat(fee.priceEuro)).join(", ")
                : "-"
            }
          />
          <FunctionField
            label="Платено обучение"
            render={plan => `${plan.paidEducationFees?.length ?? 0} реда`}
          />
          <FunctionField
            label="Вид такса"
            render={plan => formatTaxTypes(plan.paidEducationFees, taxTypes)}
          />
        </Datagrid>
      </ListContextProvider>
    </ConfirmModal>
  );
};

export default AnnualFeePlansHistoryDialog;
